import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Theme } from 'shared';
import { api } from './api.js';
import { useSession } from './session.js';

/**
 * `data-theme` on `<html>`, driven by an explicit user choice (styles.css has
 * no `prefers-color-scheme` block on purpose).
 *
 * The admin account is the one exception: `users_self_update` cannot reach
 * the admin's row at all — see its comment in rls-policies.sql — so
 * `PATCH /api/me` refuses a theme change for that one actor. Rather than the
 * toggle silently failing for the admin, this provider keeps their
 * preference in localStorage instead, browser-local rather than following
 * the account. Everyone else is DB-backed via `/api/me`, already loaded by
 * SessionProvider, so there is nothing to fetch here.
 */

const STORAGE_KEY = 'perlaprop-theme';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (next: Theme) => void;
  /** Set when a non-admin staff member's theme PATCH fails; cleared on retry. */
  error: unknown;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readLocalTheme(): Theme {
  return window.localStorage.getItem(STORAGE_KEY) === 'DARK' ? 'DARK' : 'LIGHT';
}

function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme.toLowerCase();
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { state, refresh } = useSession();
  const actor = state.status === 'signed-in' ? state.me.actor : null;
  const isAdmin = actor?.staffRole === 'ADMIN';

  // The admin's theme lives only in this browser, so it has to be read
  // before the server round trip that resolves everyone else's — otherwise
  // the admin sees a flash of the wrong theme on every load.
  const [theme, setThemeState] = useState<Theme>(() => readLocalTheme());
  const [error, setError] = useState<unknown>(null);

  // Once the session resolves, non-admin staff adopt the server's value —
  // the source of truth for an account that follows them across devices.
  useEffect(() => {
    if (actor && !isAdmin) setThemeState(actor.theme);
  }, [actor, isAdmin]);

  // Theme is a staff concept — the setting only exists in StaffLayout. A
  // portal actor (or a signed-out visitor) sharing this browser must never
  // inherit whatever a staff member last chose here, so only STAFF actually
  // gets the stored/selected theme applied; everyone else always renders
  // light regardless of what is sitting in localStorage or state.
  const effectiveTheme: Theme = actor?.actorType === 'STAFF' ? theme : 'LIGHT';

  useEffect(() => {
    applyTheme(effectiveTheme);
  }, [effectiveTheme]);

  const setTheme = useCallback(
    (next: Theme) => {
      const previous = theme;
      setError(null);
      setThemeState(next);
      if (isAdmin) {
        window.localStorage.setItem(STORAGE_KEY, next);
        return;
      }
      // Optimistic: the panel already shows `next` immediately. A failure
      // reverts the local state and records the error for the settings
      // panel to render — nothing awaits this call directly.
      void api('/me', { method: 'PATCH', body: JSON.stringify({ theme: next }) })
        .then(() => refresh())
        .catch((caught: unknown) => {
          setThemeState(previous);
          setError(caught);
        });
    },
    [isAdmin, refresh, theme],
  );

  const value = useMemo(() => ({ theme, setTheme, error }), [theme, setTheme, error]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside a ThemeProvider');
  return context;
}
